// Reusable brochure section builders, composed by each event type's planPages.
// Pure data helpers — they return { kind, props } | null (or arrays) and never
// import from eventTypes (keeps the planPages dispatcher cycle-free). Page
// components that accept optional `heading`/`title`/`ctaLabel` props let one
// component serve many vocabularies (e.g. "Topics" vs "Highlights").

// Drop empty entries and flatten the nested arrays gallerySection produces.
export function compose(...sections) {
  return sections.flat().filter(Boolean);
}

export function coverSection(event, { ctaLabel } = {}) {
  const details = event.details || {};
  return { kind: "cover", props: { details, isFirst: true, ctaLabel } };
}

export function aboutSection(details, { heading } = {}) {
  if (!details.description && !details.cmeCredits && !details.accreditedBy) return null;
  return { kind: "details", props: { details, heading } };
}

export function gallerySection(details) {
  return (details.customSlides || [])
    .filter((slide) => slide?.title || slide?.body || slide?.image)
    .map((slide) => ({ kind: "customSlide", props: { slide } }));
}

export function chipsSection(details, { heading } = {}) {
  if (!(details.topics || []).length) return null;
  return { kind: "topics", props: { details, heading } };
}

export function speakersSection(details, { heading } = {}) {
  if (!(details.speakers || []).length) return null;
  return { kind: "speakers", props: { details, heading } };
}

// `kind` is the page kind ("schedule" | "workshops") — kept distinct so the
// wizard's previewPage focus-scrolling still finds the right page. `title`
// overrides the heading so the same page can read as "Itinerary", "Events", etc.
export function agendaSection(details, key, { kind = key, title } = {}) {
  if (!(details[key] || []).length) return null;
  return { kind, props: { details, title } };
}

export function rsvpSection(event) {
  const details = event.details || {};
  if (!details.collectEventRegistrations || !(details.eventRegistrationFields || []).length) return null;
  return { kind: "eventRegistration", props: { details, slug: event.slug || event.id } };
}

export function subscribeSection(event) {
  const details = event.details || {};
  if (!details.collectPreRegistration) return null;
  return { kind: "subscribe", props: { details, slug: event.slug || event.id } };
}

export function closingSection(details, { heading, ctaLabel } = {}) {
  return { kind: "closing", props: { details, heading, ctaLabel } };
}

// The historical sequence, preserved exactly for types without a custom plan
// (Conference / Medical CME). Order matches the previous global planPages.
export function defaultPlanPages(event) {
  const details = event.details || {};
  return compose(
    coverSection(event),
    aboutSection(details),
    gallerySection(details),
    chipsSection(details),
    speakersSection(details),
    agendaSection(details, "schedule", { title: "Daywise Schedule" }),
    agendaSection(details, "workshops", { title: "Workshops" }),
    rsvpSection(event),
    subscribeSection(event),
    closingSection(details),
  );
}
